import { useEffect, useState, useRef } from "react";
import {
    Navigate, Route, Routes, useNavigate, useSearchParams
} from "react-router-dom";

import CookieConsent from "react-cookie-consent";
import { HeaderBar, NavigationBar, PrivateRoute } from "./components";
import { Role } from "./utils";

import { useAtom } from "@dbeining/react-atom";
import { Container, Image, Spinner, Stack } from "react-bootstrap";
import { accessDeniedAtom, updateUserStateAtomToLocalStorage, userAtom } from "./atoms";
import { useViewport } from "./contexts";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage/AcceptInvitationPage";
import AccessDeniedPage from "./pages/AccessDeniedPage/AccessDeniedPage";
import { AdminPage } from "./pages/AdminPage/AdminPage";
import { AllTasksPage } from "./pages/AllTasksPage/AllTasksPage";
import { CalendarPage } from "./pages/CalendarPage/CalendarPage";
import ChecklistSettingsPage from "./pages/ChecklistSettingsPage/ChecklistSettingsPage";
import { CompanyInfoPage } from "./components/features/company";
import { DocumentLibraryPage } from "./pages/DocumentLibraryPage/DocumentLibraryPage";
import EmailTemplateEditPage from "./pages/EmailTemplate/EmailTemplateEditPage";
import { EmailTemplateListPage } from "./pages/EmailTemplate/EmailTemplateListPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage/ForgotPasswordPage";
import { InvitationsPage } from "./pages/InvitationsPage/InvitationsPage";
import { MyCalendarPage } from "./pages/MyCalendarPage/MyCalendarPage";
import { DashboardPage } from "./pages/DashboardPage/DashboardPage";
import { OperationsHubPage } from "./pages/OperationsHubPage/OperationsHubPage";
import { MovementPartiesHubPage } from "./pages/MovementPartiesHubPage/MovementPartiesHubPage";
import { ReferenceDataHubPage } from "./pages/ReferenceDataHubPage/ReferenceDataHubPage";
import { SystemHubPage } from "./pages/SystemHubPage/SystemHubPage";
import { PlaceholderPage } from "./pages/PlaceholderPage/PlaceholderPage";
import { OrganisationsPage } from "./pages/OrganisationsPage/OrganisationsPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage/ResetPasswordPage";
import { SessionsPage } from "./pages/SessionsPage/SessionsPage";
import { SettingsPage } from "./pages/SettingsPage/SettingsPage";
import { SignInPage } from "./pages/SignInPage/SignInPage";
import { TaskDetailPage } from "./pages/Task/TaskDetailPage";
import { TaskListPage } from "./pages/Task/TaskListPage";
import { TwoFASetupPage } from "./pages/TwoFASetupPage/TwoFASetupPage";
import { UserPage } from "./pages/UserPage/UserPage";
import { UsersPage } from "./pages/UsersPage/UsersPage";
import { WebsiteSettingListPage } from "./pages/WebsiteSetting/WebsiteSettingListPage";
import { authenticationService } from "./services";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";
import "./styles/App.css";
import { ToastContainer } from "react-toastify";

const adminRoles = [Role.Admin, Role.SystemAdmin];

const App = () => {
    let navigate = useNavigate();
    let [searchParams] = useSearchParams();
    const id: string = searchParams.get("id") || "";
    const currentActionAccessDenied = useAtom(accessDeniedAtom);
    const [loadingUser, setLoadingUser] = useState(true);
    const currentUser = useAtom(userAtom);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const lastManualState = useRef<boolean | null>(null);

    const { width } = useViewport();
    const lastWidth = useRef<number>(width);
    const breakpoint = 768;

    useEffect(() => {
        const wasInMediumRange = lastWidth.current >= 768 && lastWidth.current < 1065;
        const wasInLargeRange = lastWidth.current >= 1065;
        const wasInMobileRange = lastWidth.current < 768;

        const isInMediumRange = width >= 768 && width < 1065;
        const isInLargeRange = width >= 1065;
        const isInMobileRange = width < 768;

        if (isInMobileRange && !wasInMobileRange) {
            setIsSidebarCollapsed(false);
        } else if (isInMediumRange && !wasInMediumRange) {
            setIsSidebarCollapsed(true);
            lastManualState.current = null;
        } else if (isInLargeRange && !wasInLargeRange) {
            setIsSidebarCollapsed(false);
            lastManualState.current = null;
        } else if (isInMediumRange && wasInMobileRange) {
            if (lastManualState.current !== null) {
                setIsSidebarCollapsed(lastManualState.current);
            } else {
                setIsSidebarCollapsed(true);
            }
        } else if (isInLargeRange && wasInMobileRange) {
            if (lastManualState.current !== null) {
                setIsSidebarCollapsed(lastManualState.current);
            } else {
                setIsSidebarCollapsed(false);
            }
        }

        lastWidth.current = width;
    }, [width]);

    useEffect(() => {
        if (width >= 768) {
            const offcanvasElement = document.querySelector('.offcanvas.show');
            if (offcanvasElement) {
                const offcanvas = (window as any).bootstrap?.Offcanvas.getInstance(offcanvasElement);
                if (offcanvas) {
                    offcanvas.hide();
                }
            }
        }
    }, [width]);

    useEffect(() => {
        updateUserStateAtomToLocalStorage();

        authenticationService.checkAuthorised().then(() => {
            setLoadingUser(false);
        });
    }, []);

    useEffect(() => {
        if (currentActionAccessDenied) {
            navigate("/accessdenied", { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentActionAccessDenied]);

    if (loadingUser) {
        return (
            <div className="d-flex align-content-center justify-content-center flex-wrap vh-100">
                <Stack className="justify-content-center mb-5" gap={3}>
                    <Image
                        src="/images/froom-waste-tracker-logo.svg"
                        className="mx-auto froom-logo"
                    />
                    <Spinner animation={"border"} variant="info" className="mx-auto" />
                </Stack>
            </div>
        );
    }

    const isFullyAuthenticated = currentUser &&
        (!currentUser.advisories || !currentUser.advisories.twoFaSetupRequired);

    const sidebarWidth = isSidebarCollapsed ? "5.5rem" : "17rem";

    const handleTransitionStart = () => {
        setIsTransitioning(true);
    };

    const handleTransitionEnd = () => {
        setIsTransitioning(false);
    };

    const handleSidebarToggle = (isCollapsed: boolean) => {
        setIsSidebarCollapsed(isCollapsed);
        lastManualState.current = isCollapsed;
    };

    return (
        <>
            <div className="d-flex flex-nowrap min-height-100vh">
                {isFullyAuthenticated && width >= breakpoint && (
                    <div
                        className="d-flex flex-column width-ease"
                        style={{
                            width: sidebarWidth
                        }}
                    >
                        <NavigationBar
                            setShowMenu={() => { }}
                            onCollapsedChange={handleSidebarToggle}
                            onTransitionStart={handleTransitionStart}
                            onTransitionEnd={handleTransitionEnd}
                            isCollapsed={isSidebarCollapsed}
                        />
                    </div>
                )}

                <div className="flex-column flex-grow-1 flex-1">
                    {isFullyAuthenticated && (
                        <div id="header-container" className={isTransitioning ? "transitioning" : ""}>
                            <HeaderBar
                                isSidebarCollapsed={isSidebarCollapsed}
                                setIsSidebarCollapsed={setIsSidebarCollapsed}
                                onTransitionStart={handleTransitionStart}
                                onTransitionEnd={handleTransitionEnd}
                            />
                        </div>
                    )}
                    <Container fluid className={isFullyAuthenticated && width >= breakpoint ? "p-4" : "p-0"} style={{
                        ...(isFullyAuthenticated && width >= breakpoint ? {
                            float: "right",
                            width: `calc(100% - ${sidebarWidth})`,
                            transition: "width 0.3s ease"
                        } : {})
                    }}>
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={
                                <PrivateRoute>
                                    <DashboardPage />
                                </PrivateRoute>
                            } />
                            <Route path="/operations" element={
                                <PrivateRoute>
                                    <OperationsHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/movement-parties" element={
                                <PrivateRoute>
                                    <MovementPartiesHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/reference-data" element={
                                <PrivateRoute>
                                    <ReferenceDataHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/system" element={
                                <PrivateRoute>
                                    <SystemHubPage />
                                </PrivateRoute>
                            } />
                            <Route path="/sites" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Sites" />
                                </PrivateRoute>
                            } />
                            <Route path="/customers" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Customers" />
                                </PrivateRoute>
                            } />
                            <Route path="/locations" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Locations" />
                                </PrivateRoute>
                            } />
                            <Route path="/vehicles" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Vehicles" />
                                </PrivateRoute>
                            } />
                            <Route path="/hauliers" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Hauliers" />
                                </PrivateRoute>
                            } />
                            <Route path="/tippers" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Tippers" />
                                </PrivateRoute>
                            } />
                            <Route path="/processors" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Processors" />
                                </PrivateRoute>
                            } />
                            <Route path="/waste-types" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Waste Types" />
                                </PrivateRoute>
                            } />
                            <Route path="/ewc-codes" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="EWC Codes" />
                                </PrivateRoute>
                            } />
                            <Route path="/disposal-recovery-codes" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Disposal/Recovery Codes" />
                                </PrivateRoute>
                            } />
                            <Route path="/physical-states" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Physical States" />
                                </PrivateRoute>
                            } />
                            <Route path="/pre-treatment" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Pre-Treatment" />
                                </PrivateRoute>
                            } />
                            <Route path="/source-activity" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Source Activity" />
                                </PrivateRoute>
                            } />
                            <Route path="/geography" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Geography" />
                                </PrivateRoute>
                            } />
                            <Route path="/flags" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Flags (Municipal / Degradable)" />
                                </PrivateRoute>
                            } />
                            <Route path="/audit-logs" element={
                                <PrivateRoute>
                                    <PlaceholderPage title="Audit Logs" />
                                </PrivateRoute>
                            } />
                            <Route path="/mytasks" element={
                                <PrivateRoute>
                                    <TaskListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/tasks" element={
                                <PrivateRoute roles={adminRoles}>
                                    <AllTasksPage />
                                </PrivateRoute>
                            } />
                            <Route path="/task/:id" element={
                                <PrivateRoute>
                                    <TaskDetailPage />
                                </PrivateRoute>
                            } />
                            <Route path="/diary" element={
                                <PrivateRoute>
                                    <CalendarPage />
                                </PrivateRoute>
                            } />
                            <Route path="/mycalendar" element={
                                <PrivateRoute>
                                    <MyCalendarPage />
                                </PrivateRoute>
                            } />
                            <Route path="/documents" element={
                                <PrivateRoute>
                                    <DocumentLibraryPage />
                                </PrivateRoute>
                            } />
                            <Route path="/checklists" element={
                                <PrivateRoute roles={adminRoles}>
                                    <ChecklistSettingsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/EmailTemplates" element={
                                <PrivateRoute roles={adminRoles}>
                                    <EmailTemplateListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/EmailTemplates/edit" element={
                                <PrivateRoute roles={adminRoles}>
                                    <EmailTemplateEditPage />
                                </PrivateRoute>
                            } />
                            <Route path="/settings" element={
                                <PrivateRoute>
                                    <SettingsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/users" element={
                                <PrivateRoute roles={adminRoles}>
                                    <UsersPage />
                                </PrivateRoute>
                            } />
                            <Route path="/Users" element={<Navigate to="/users" replace />} />
                            <Route path="/user" element={
                                <PrivateRoute>
                                    <UserPage />
                                </PrivateRoute>
                            } />
                            <Route path="/company" element={
                                <PrivateRoute roles={adminRoles}>
                                    <CompanyInfoPage />
                                </PrivateRoute>
                            } />
                            <Route path="/websitesettings" element={
                                <PrivateRoute roles={adminRoles}>
                                    <WebsiteSettingListPage />
                                </PrivateRoute>
                            } />
                            <Route path="/organisations" element={
                                <PrivateRoute roles={adminRoles}>
                                    <OrganisationsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/administration" element={
                                <PrivateRoute roles={adminRoles}>
                                    <AdminPage />
                                </PrivateRoute>
                            } />
                            <Route path="/invitations" element={
                                <PrivateRoute roles={adminRoles}>
                                    <InvitationsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/sessions" element={
                                <PrivateRoute roles={adminRoles}>
                                    <SessionsPage />
                                </PrivateRoute>
                            } />
                            <Route path="/accessdenied" element={<AccessDeniedPage />} />
                            <Route path="/signin" element={<SignInPage />} />
                            <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
                            <Route path="/twofactorSetup" element={<TwoFASetupPage />} />
                            <Route path="/acceptinvitation" element={<AcceptInvitationPage id={id} />} />
                            <Route path="/resetpassword" element={<ResetPasswordPage id={id} />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Container>
                </div>
            </div>
            <CookieConsent
                location="bottom"
                cookieName="FroomWasteTrackerCookieConsent"
                containerClasses="cookie-consent-banner"
                style={{
                    zIndex: 7,
                    backgroundColor: "var(--bg-tertiary)",
                    alignItems: "center",
                    padding: "12px 16px",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
                contentStyle={{
                    flex: "1 1 280px",
                    color: "var(--text-on-tertiary-3)",
                    margin: 0,
                }}
                buttonText="Accept all cookies"
                buttonStyle={{
                    backgroundColor: "var(--black)",
                    color: "var(--white)",
                    border: "1px solid var(--black)",
                    padding: "10px 18px",
                    fontWeight: 600,
                    borderRadius: "0.375rem",
                }}
                enableDeclineButton
                declineButtonText="More information"
                declineButtonStyle={{
                    backgroundColor: "var(--white)",
                    color: "var(--black)",
                    border: "1px solid var(--black)",
                    padding: "10px 18px",
                    fontWeight: 600,
                    borderRadius: "0.375rem",
                }}
                flipButtons
            >
                <h3 className="cookie-consent-heading">This website uses cookies</h3>
                Cookies help us deliver the best experience on our website. By using our
                website, you agree to the use of cookies.
            </CookieConsent>
            <ToastContainer />
        </>
    );
};
export { App };
